import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { splitSample } from "@/lib/data";
import { GuestView } from "@/components/guest/GuestView";
import { FullLoader } from "@/lib/auth";

const tokenKey = (slug) => `hk_unlock_${slug}`;

function Message({ title, body, testId }) {
  return (
    <div className="grid min-h-screen place-items-center bg-[#F4EFEA] px-6 text-center" data-testid={testId}>
      <div className="max-w-sm">
        <h1 className="font-display text-3xl">{title}</h1>
        <p className="mt-3 text-muted-foreground">{body}</p>
        <Link to="/" className="mt-6 inline-block text-sm font-semibold text-primary">hostkey.online</Link>
      </div>
    </div>
  );
}

function SampleBar() {
  return (
    <div className="sticky top-0 z-40 flex items-center justify-between gap-3 bg-[#151312] px-4 py-2.5 text-sm text-white" data-testid="sample-banner">
      <span>Sample guest page. Try the form, nothing is sent.</span>
      <Link to="/" className="font-semibold text-[#E4A853]" data-testid="sample-back-link">Back</Link>
    </div>
  );
}

export default function GuestPage({ sample, keyholder }) {
  const { slug: slugParam, code } = useParams();
  const [state, setState] = useState(sample ? { sample: splitSample() } : null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (sample) return;
    const url = keyholder ? `/public/k/${code}` : `/public/p/${slugParam}`;
    api.get(url).then(async (r) => {
      const d = r.data;
      if (!d.paused && d.locked) {
        const tok = localStorage.getItem(tokenKey(d.slug));
        if (tok) {
          try {
            const i = await api.get(`/public/p/${d.slug}/info`, { params: { token: tok } });
            d.info = i.data.info;
            d.returned = true;
          } catch {
            localStorage.removeItem(tokenKey(d.slug));
          }
        }
      }
      setState(d);
    }).catch((e) => setError(errMsg(e, "This guest page could not be found.")));
  }, [sample, keyholder, code, slugParam]);

  useEffect(() => {
    const name = state?.property?.name || state?.sample?.property?.name;
    if (name) document.title = `${name} · Guest page`;
  }, [state]);

  if (error) return <Message title="Page not found" body={error} testId="guest-not-found" />;
  if (!state) return <FullLoader />;
  if (state.paused) return <Message title="Temporarily unavailable" body={`The guest page for ${state.name} is paused right now. Please contact your host directly.`} testId="guest-paused" />;

  if (state.sample) {
    const { property, info } = state.sample;
    const wait = () => new Promise((r) => setTimeout(r, 700));
    return (
      <>
        <SampleBar />
        <GuestView property={property} locked info={null}
          onCheckin={async () => { await wait(); return { ref: "HK-SAMPLE", info }; }}
          onReturning={async () => { await wait(); return { name: "friend", info }; }} />
      </>
    );
  }

  const slug = state.slug;
  const onCheckin = async (form, file) => {
    const fd = new FormData();
    fd.append("data", JSON.stringify(form));
    if (state.keyholder) fd.append("kh", state.keyholder.code);
    if (file) fd.append("id_photo", file);
    try {
      const r = await api.post(`/public/p/${slug}/checkin`, fd);
      localStorage.setItem(tokenKey(slug), r.data.token);
      return r.data;
    } catch (e) {
      throw new Error(errMsg(e, "Could not send the form. Please try again."));
    }
  };
  const onReturning = async (last4) => {
    try {
      const r = await api.post(`/public/p/${slug}/returning`, { last4 });
      localStorage.setItem(tokenKey(slug), r.data.token);
      return r.data;
    } catch (e) {
      throw new Error(errMsg(e, "We couldn't find you. Please fill in the form."));
    }
  };

  return <GuestView property={state.property} info={state.info || null} locked={state.locked} onCheckin={onCheckin} onReturning={onReturning} unlockedRef={state.returned ? { ref: null, name: "" } : null} />;
}
